"use client";

import { ComponentPropsWithoutRef, FC } from "react";
import { useInViewVideo } from "../../hooks/useInViewVideo";

export type MediaBlockData = {
  src: string;
  alt?: string; // alt is optional since video might not have it
  type: "image" | "video";
  poster?: string;
};

type MediaBlockProps = {
  data: MediaBlockData;
} & (ComponentPropsWithoutRef<"img"> | ComponentPropsWithoutRef<"video">);

const MediaBlock: FC<MediaBlockProps> = ({ data, ...rest }) => {
  if (data.type === "image") {
    return (
      <img
        src={data.src}
        alt={data.alt}
        loading="lazy"
        decoding="async"
        {...(rest as ComponentPropsWithoutRef<"img">)}
        className={`${rest.className} rounded-2xl isolate overflow-hidden`}
      />
    );
  }

  if (data.type === "video") {
    return <InViewVideo data={data} {...rest} />;
  }

  return null;
};

const InViewVideo: FC<MediaBlockProps & { data: MediaBlockData }> = ({
  data,
  ...rest
}) => {
  const ref = useInViewVideo<HTMLVideoElement>();
  // Convention used across the marketing surfaces:
  //   - With a `poster` JPEG → `preload="none"` (the poster already fills
  //     the rectangle, saves bandwidth on the LCP-critical hero).
  //   - Without a `poster`    → `preload="metadata"` so the browser lazily
  //     pulls just enough video bytes (~hundreds of KB, post-interactive)
  //     to render the first frame natively, instead of leaving an empty
  //     grey box until the user scrolls the section into view.
  // Callers may override via `rest.preload`.
  const defaultPreload: "none" | "metadata" = data.poster ? "none" : "metadata";
  return (
    <video
      ref={ref}
      src={data.src}
      poster={data.poster}
      loop
      muted
      playsInline
      preload={defaultPreload}
      disableRemotePlayback
      disablePictureInPicture
      {...(rest as ComponentPropsWithoutRef<"video">)}
      className={`${rest.className} rounded-2xl isolate overflow-hidden`}
    >
      {/* Empty captions track to match the convention used across the
          marketing surfaces (Hero, ShortsStack, AI Reframe). The clips are
          muted product demos with no spoken content; an empty VTT signals
          to AT that no captions are available rather than leaving the
          captions menu missing entirely. */}
      <track
        kind="captions"
        srcLang="en"
        label="English"
        src="/captions/empty.vtt"
        default
      />
    </video>
  );
};

export default MediaBlock;
